/**
 * The pin a Messenger customer drops on a map, on its way back to their thread.
 *
 * Deliberately does **not** import `./client`. Every other service in this app goes
 * through it and inherits a bearer token, a refresh-on-401 retry, and a session-expired
 * handler that can redirect to sign-in. Every one of those is wrong here: this page is
 * opened by somebody who has no account, from inside Messenger, on a phone that has never
 * signed in to anything of ours. A 401 retry loop or a redirect to `/` would be the whole
 * feature failing.
 *
 * So it is a bare fetch. Not a shortcut — the absence of auth code is the guarantee.
 *
 * **`getTrackedOrder` was here**, calling `GET /api/track/:token` for the public tracking
 * page. Both are gone. The shared link is now the customer's own order screen, and it
 * reads through `orderService.getSharedOrder` — which is a bare fetch for exactly the
 * reasoning above, inherited rather than reinvented.
 */

const BASE_URL = import.meta.env.VITE_API_URL

if (!BASE_URL) {
  throw new Error(
    'VITE_API_URL is not set. Copy .env.example to .env and restart the dev server — ' +
      'Vite only reads env files at startup.',
  )
}

export class TrackError extends Error {
  constructor(message, status) {
    super(message)
    this.name = 'TrackError'
    this.status = status
  }
}

/**
 * `POST /api/messenger/pin`
 *
 * The pin from the map page, on its way back to a Messenger thread. Same bare fetch and
 * the same reason: this page is opened by somebody with no account, from inside Messenger.
 * The signed `token` is the only credential, and it names one conversation and nothing
 * else — it cannot place an order or read one.
 */
export async function sendPin({ token, latitude, longitude, address }) {
  const response = await fetch(`${BASE_URL}/api/messenger/pin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ token, latitude, longitude, address }),
  })

  if (!response.ok) {
    throw new TrackError('Hindi po naipadala ang lokasyon.', response.status)
  }

  return true
}
